import React, { useEffect, useContext, useState, useCallback } from 'react';
import styled from 'styled-components';
import { CartContext } from '@com/core/common/contexts/cartContextDaily';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import { debounce } from 'lodash';
import Loader from './Loader';

type TProps = {
    eventLocation: 'page_catalog' | 'page_static' | 'recos_page-detail' | 'recos_page-static';
    offerCode: string;
    sku: string;
    isRecommended?: boolean;
    stock?: number;
};

const QuickAddToCart = ({ eventLocation, isRecommended, offerCode, sku, stock }: TProps): JSX.Element => {
    // cli - todo tidy these in the cart context
    const { getItemQty, isAdded, removeItem, updateQuantity } = useContext(CartContext);
    const { addresses, customer, showLoginModal } = useContext(CustomerContext);
    const { hasValidAddress } = useContext(DailyContext);
    const [isLoading, setLoading] = useState(false);

    const isAddedItem = isAdded(offerCode);
    const addedQty = isAddedItem ? getItemQty(offerCode) : 0;

    const [currentCount, setCurrentCount] = useState(0);

    useEffect(() => {
        setCurrentCount(addedQty);
    }, [addedQty]);

    const handleUpdateQty = async (newQty: number): Promise<void> => {
        if (customer) {
            if (!hasValidAddress) {
                // sending the product details object here and saving it in the context with addresses.
                const productToAdd = {
                    offerCode,
                    quantity: newQty,
                    sku,
                    showModal: false,
                    eventLocation,
                    attachments: [],
                };
                addresses.toggleShowAddressModal(true, true, productToAdd);
            } else {
                setLoading(true);
                if (newQty === 0) {
                    handleRemoveItem();
                } else {
                    await updateQuantity(offerCode, newQty);
                }
                setLoading(false);
            }
        } else {
            showLoginModal(true);
        }
    };

    const handleRemoveItem = async (): Promise<void> => {
        setLoading(true);
        await removeItem(offerCode, 'cart', sku);
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateCount = useCallback(
        debounce(count => {
            handleUpdateQty(count);
        }, 1500),
        [],
    );

    return (
        <Container>
            {!currentCount || isRecommended ? (
                <AddToCartButton
                    type="button"
                    onClick={() => {
                        setCurrentCount(currentCount + 1);
                        updateCount(currentCount + 1);
                    }}>
                    {isLoading ? (
                        <Loader type="spin" color="brand" />
                    ) : (
                        <img src={`${CDN_IMG_BASE}/simpleIcons/plus-bodyAlt.svg`} alt="Add" />
                    )}
                </AddToCartButton>
            ) : (
                <QtyChanger>
                    {currentCount === 1 ? (
                        <button
                            type="button"
                            className="removal"
                            disabled={isLoading}
                            onClick={() => {
                                setCurrentCount(currentCount - 1);
                                updateCount(currentCount - 1);
                            }}>
                            <img src={`${CDN_IMG_BASE}/simpleIcons/trash-2.svg`} alt="Remove" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="subtraction"
                            disabled={isLoading}
                            onClick={() => {
                                setCurrentCount(currentCount - 1);
                                updateCount(currentCount - 1);
                            }}>
                            <img src={`${CDN_IMG_BASE}/simpleIcons/minus-brandPrimary.svg`} alt="Minus" />
                        </button>
                    )}
                    <div className="valueContainer">
                        {isLoading ? (
                            <Loader type="spin" color="brand" />
                        ) : (
                            <span className="value">{currentCount}</span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="addition"
                        onClick={() => {
                            setCurrentCount(currentCount + 1);
                            updateCount(currentCount + 1);
                        }}
                        disabled={isLoading || currentCount === stock}>
                        <img src={`${CDN_IMG_BASE}/simpleIcons/plus-brandPrimary.svg`} alt="Add" />
                    </button>
                </QtyChanger>
            )}
        </Container>
    );
};

const Container = styled.div`
    position: absolute;
    bottom: 0;
    ${({ theme: { right } }): string => `${right}: 8px`};
`;

const AddToCartButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 36px;
    background-color: ${({ theme }): string => theme.colors.brandPrimary};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform ease-in-out 0.2s;

    > img {
        width: 18px;
    }

    &:hover {
        transform: scale(1.15);
    }
`;

const QtyChanger = styled.div`
    display: flex;
    align-items: center;
    background-color: rgba(242, 244, 251, 0.8);
    border-radius: 32px;
    padding: 1px;

    > button {
        width: 36px;
        height: 36px;
        border-radius: 36px;
        border: 1px solid ${({ theme }): string => theme.colors.brandPrimary};
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        display: flex;
        align-items: center;
        justify-content: center;

        &[disabled] {
            cursor: not-allowed;
            opacity: 0.4;
        }

        &.removal {
            border-color: ${({ theme }): string => theme.colors.border};

            > img {
                width: 15px;
            }
        }

        > img {
            width: 16px;
            transition: transform ease-in-out 0.2s;
        }

        &:not([disabled]):hover {
            > img {
                transform: scale(0.8);
            }
        }

        &.addition:not([disabled]):hover {
            > img {
                transform: scale(1.25);
            }
        }
    }

    .valueContainer {
        width: 40px;
        text-align: center;

        .value {
            font-weight: 600;
            font-size: 15px;
        }
    }
`;

export default QuickAddToCart;
